import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { formatInsightDate, getRecommendedArticles, topicFor } from '@/data/insights';
import { Reveal } from '../Reveal';
import { useLiveTick } from '@/lib/liveContent';

// Section 6 — Recommended reading. Compact, list-style layout deliberately
// distinct from the main article cards. Selections are centrally configured
// (RECOMMENDED_ARTICLE_SLUGS). Labelled "Recommended reading" — never
// "Most Read" — unless genuine analytics view data exists.
export const RecommendedReading = () => {
  useLiveTick();
  const recommended = getRecommendedArticles(4);
  if (recommended.length === 0) return null;

  return (
    <section aria-labelledby="recommended-heading" data-testid="recommended-reading" className="bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="flex items-center gap-3">
            <span className="font-mono text-xs font-medium tracking-widest text-gold-700">04</span>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-700">Handpicked</span>
          </p>
          <h2 id="recommended-heading" data-testid="recommended-heading" className="mt-4 text-balance text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl">
            Recommended reading
          </h2>
        </div>

        <ol className="mt-10 divide-y divide-slate-200 border-y border-slate-200" data-testid="recommended-list">
          {recommended.map((art, i) => {
            const topic = topicFor(art.primaryTopic);
            return (
              <li key={art.id || art.slug}>
                <Reveal delay={i * 0.06}>
                  <Link
                    to={`/insights/${art.slug}`}
                    data-testid={`recommended-${art.slug}`}
                    className="group flex items-center gap-5 py-5 transition-colors sm:gap-8"
                  >
                    <span className="font-mono text-2xl font-medium text-slate-300 transition-colors group-hover:text-gold-700" aria-hidden="true">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        {topic && (
                          <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: topic.dark }}>
                            {topic.label}
                          </span>
                        )}
                        <time dateTime={art.publishedAt} className="text-xs font-medium text-slate-400">
                          {formatInsightDate(art.publishedAt)}
                        </time>
                      </div>
                      <p className="mt-1.5 truncate text-base font-extrabold tracking-tight text-navy-900 transition-colors group-hover:text-navy-600 sm:text-lg">
                        {art.title}
                      </p>
                    </div>
                    <ArrowRight className="h-5 w-5 shrink-0 text-slate-300 transition-all duration-200 group-hover:translate-x-1 group-hover:text-navy-900" aria-hidden="true" />
                  </Link>
                </Reveal>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
};
